import { Archive, ArchiveRestore, Plus, Trash2, X } from "lucide-react";
import {
  useCanAccess,
  useGetList,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useMemo, useRef, useState } from "react";
import { AccessDenied } from "@/components/admin/access-denied";
import { useIsMobile } from "@/hooks/use-mobile";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { SettingsTabs } from "../settings/SettingsTabs";
import type { Pipeline, Project, Sale, SalesProject } from "../types";
import { AccessSelect } from "./AccessSelect";
import { PipelineEditor } from "./PipelineEditor";
import { byPosition } from "./byPosition";
import { presetAccess, useProjectAdmin } from "./useProjectAdmin";

const LIST = { pagination: { page: 1, perPage: 1000 } };

/**
 * Настройки → Проекты (owner only): projects with their own lead pipeline and
 * deal pipelines, the stages of each, and who works in the project.
 */
export const ProjectSettings = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "projects",
    action: "edit",
  });
  const isMobile = useIsMobile();
  if (isPending) return null;
  if (!canAccess) return <AccessDenied />;
  return (
    <div className="mt-4 pb-20 flex flex-col gap-6">
      {isMobile ? null : <SettingsTabs />}
      <ProjectsEditor />
    </div>
  );
};

ProjectSettings.path = "/settings/projects";

const ProjectsEditor = () => {
  const translate = useTranslate();
  const run = useProjectAdmin();
  const { data } = useGetList<Project>("projects", {
    ...LIST,
    sort: { field: "position", order: "ASC" },
  });
  const projects = useMemo(() => [...(data ?? [])].sort(byPosition), [data]);
  const [selectedId, setSelectedId] = useState<Identifier | null>(null);
  const selected =
    projects.find((p) => p.id === selectedId) ?? projects[0] ?? null;
  const [newName, setNewName] = useState("");

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    const created = await run((dataProvider) =>
      dataProvider.create<Project>("projects", {
        data: {
          name,
          position: Math.max(0, ...projects.map((p) => p.position)) + 1,
        },
      }),
    );
    if (created) {
      setSelectedId(created.data.id);
      setNewName("");
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-[15rem_1fr] items-start">
      <Card>
        <CardContent className="flex flex-col gap-1">
          <h2 className="vv-heading mb-2">{translate("crm.projects.title")}</h2>
          {projects.map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={() => setSelectedId(project.id)}
              aria-current={project.id === selected?.id ? "true" : undefined}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                project.id === selected?.id
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <ColorDot color={project.color} />
              <span className="truncate flex-1">{project.name}</span>
              {project.archived ? (
                <span className="text-[0.6875rem] text-muted-foreground">
                  {translate("crm.projects.archived")}
                </span>
              ) : null}
            </button>
          ))}
          <form
            className="flex gap-1.5 mt-3"
            onSubmit={(event) => {
              event.preventDefault();
              create();
            }}
          >
            <Input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder={translate("crm.projects.new")}
              aria-label={translate("crm.projects.new")}
              className="h-8"
            />
            <Button
              type="submit"
              size="icon"
              variant="outline"
              aria-label={translate("crm.projects.create")}
            >
              <Plus className="size-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
      {selected ? <ProjectEditor key={selected.id} project={selected} /> : null}
    </div>
  );
};

/**
 * The colour is saved once it is chosen (the native «change» event: the
 * picker closed), not on every move inside the picker (React's onChange
 * follows «input» events).
 */
const ColorInput = ({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: string;
  onCommit: (color: string) => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [color, setColor] = useState(value);
  useEffect(() => setColor(value), [value]);
  const commit = useRef(onCommit);
  commit.current = onCommit;
  useEffect(() => {
    const input = ref.current;
    if (!input) return;
    const onChange = () => commit.current(input.value);
    input.addEventListener("change", onChange);
    return () => input.removeEventListener("change", onChange);
  }, []);
  return (
    <input
      ref={ref}
      type="color"
      aria-label={label}
      value={color}
      onChange={(event) => setColor(event.target.value)}
      className="size-8 shrink-0 cursor-pointer rounded border bg-transparent p-0.5"
    />
  );
};

const ColorDot = ({ color }: { color?: string | null }) => (
  <span
    className="size-2.5 shrink-0 rounded-full bg-muted-foreground"
    style={color ? { background: color } : undefined}
    aria-hidden
  />
);

const ProjectEditor = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const run = useProjectAdmin();
  const [name, setName] = useState(project.name);
  useEffect(() => setName(project.name), [project.name]);

  const update = (change: Partial<Project>) =>
    run((dataProvider) =>
      dataProvider.update<Project>("projects", {
        id: project.id,
        data: change,
        previousData: project,
      }),
    );
  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed) setName(project.name);
    else if (trimmed !== project.name) update({ name: trimmed });
  };

  return (
    <div className="flex flex-col gap-6 min-w-0">
      <Card>
        <CardContent className="flex-row flex-wrap items-center gap-3">
          <ColorInput
            label={translate("crm.projects.color")}
            value={project.color || "#686a6c"}
            onCommit={(color) => {
              if (color !== project.color) update({ color });
            }}
          />
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={saveName}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                saveName();
              }
            }}
            aria-label={translate("crm.projects.name")}
            className="h-9 flex-1 min-w-40 text-base font-semibold"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => update({ archived: !project.archived })}
          >
            {project.archived ? (
              <ArchiveRestore className="size-4" />
            ) : (
              <Archive className="size-4" />
            )}
            {translate(
              project.archived
                ? "crm.projects.unarchive"
                : "crm.projects.archive",
            )}
          </Button>
        </CardContent>
      </Card>
      <PipelinesEditor project={project} />
      <ProjectMembers project={project} />
    </div>
  );
};

/** Tabs: the lead pipeline, then the deal pipelines; the stages of the chosen one. */
const PipelinesEditor = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const run = useProjectAdmin();
  const { data } = useGetList<Pipeline>("pipelines", {
    ...LIST,
    sort: { field: "position", order: "ASC" },
    filter: { project_id: project.id },
  });
  const pipelines = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) =>
          (a.kind === "lead" ? 0 : 1) - (b.kind === "lead" ? 0 : 1) ||
          byPosition(a, b),
      ),
    [data],
  );
  const [selectedId, setSelectedId] = useState<Identifier | null>(null);
  const selected =
    pipelines.find((p) => p.id === selectedId) ?? pipelines[0] ?? null;
  const dealPipelines = pipelines.filter((p) => p.kind === "deal");

  const addPipeline = async () => {
    const created = await run((dataProvider) =>
      dataProvider.create<Pipeline>("pipelines", {
        data: {
          project_id: project.id,
          kind: "deal",
          name: translate("crm.projects.new_pipeline_name"),
          position: Math.max(0, ...dealPipelines.map((p) => p.position)) + 1,
        },
      }),
    );
    if (created) setSelectedId(created.data.id);
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <h2 className="vv-heading">{translate("crm.projects.stages")}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <nav className="flex gap-1 rounded-lg bg-muted p-1 w-fit max-w-full overflow-x-auto">
            {pipelines.map((pipeline) => (
              <button
                key={pipeline.id}
                type="button"
                onClick={() => setSelectedId(pipeline.id)}
                aria-current={pipeline.id === selected?.id ? "page" : undefined}
                className={cn(
                  "rounded-md px-3 py-1 text-sm font-medium whitespace-nowrap transition-colors",
                  pipeline.id === selected?.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {pipeline.kind === "lead"
                  ? translate("crm.projects.lead_pipeline")
                  : pipeline.name}
              </button>
            ))}
          </nav>
          <Button type="button" variant="ghost" size="sm" onClick={addPipeline}>
            <Plus className="size-4" />
            {translate("crm.projects.add_pipeline")}
          </Button>
        </div>
        {selected?.kind === "deal" ? (
          <PipelineName
            pipeline={selected}
            canDelete={dealPipelines.length > 1}
          />
        ) : null}
        {selected ? (
          <PipelineEditor key={selected.id} pipeline={selected} />
        ) : null}
      </CardContent>
    </Card>
  );
};

const PipelineName = ({
  pipeline,
  canDelete,
}: {
  pipeline: Pipeline;
  canDelete: boolean;
}) => {
  const translate = useTranslate();
  const run = useProjectAdmin();
  const [name, setName] = useState(pipeline.name);
  useEffect(() => setName(pipeline.name), [pipeline.name]);
  const [confirming, setConfirming] = useState(false);
  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) setName(pipeline.name);
    else if (trimmed !== pipeline.name)
      run((dataProvider) =>
        dataProvider.update<Pipeline>("pipelines", {
          id: pipeline.id,
          data: { name: trimmed },
          previousData: pipeline,
        }),
      );
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            save();
          }
        }}
        aria-label={translate("crm.projects.pipeline_name")}
        className="h-8 max-w-72"
      />
      {canDelete ? (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setConfirming(true)}
          >
            <Trash2 className="size-4" />
            {translate("crm.projects.delete_pipeline")}
          </Button>
          <Confirm
            isOpen={confirming}
            title="crm.projects.confirm_delete_pipeline"
            titleTranslateOptions={{ name: pipeline.name }}
            content="crm.projects.confirm_delete_hint"
            confirm="ra.action.delete"
            confirmColor="warning"
            onClose={() => setConfirming(false)}
            onConfirm={() => {
              setConfirming(false);
              run((dataProvider) =>
                dataProvider.delete("pipelines", {
                  id: pipeline.id,
                  previousData: pipeline,
                }),
              );
            }}
          />
        </>
      ) : null}
    </div>
  );
};

/** Who works in the project and what they see there. */
const ProjectMembers = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const run = useProjectAdmin();
  const { data: sales } = useGetList<Sale>("sales", {
    ...LIST,
    sort: { field: "last_name", order: "ASC" },
  });
  const { data: rows } = useGetList<SalesProject>("sales_projects", {
    ...LIST,
    filter: { project_id: project.id },
  });
  const employees = (sales ?? []).filter(
    (s) => !s.disabled && s.role !== "owner" && !s.administrator,
  );
  const members = (rows ?? [])
    .map((row) => ({
      row,
      sale: employees.find((s) => String(s.id) === String(row.sales_id)),
    }))
    .filter((m): m is { row: SalesProject; sale: Sale } => !!m.sale);
  const others = employees.filter(
    (s) => !members.some((m) => m.sale.id === s.id),
  );

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <h2 className="vv-heading">{translate("crm.projects.members")}</h2>
        {!members.length ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.projects.no_members")}
          </p>
        ) : (
          <ul className="flex flex-col divide-y">
            {members.map(({ row, sale }) => (
              <li key={row.id} className="flex items-center gap-3 py-1.5">
                <span className="flex-1 min-w-0 truncate text-sm">
                  {sale.first_name} {sale.last_name}
                  <span className="text-muted-foreground">
                    {" · "}
                    {translate(`crm.projects.roles.${sale.role ?? "manager"}`)}
                  </span>
                </span>
                <AccessSelect
                  allowNone={false}
                  label={`${sale.first_name} ${sale.last_name}`}
                  value={row.access}
                  onChange={(access) =>
                    run((dataProvider) =>
                      dataProvider.update<SalesProject>("sales_projects", {
                        id: row.id,
                        data: { access: access as SalesProject["access"] },
                        previousData: row,
                      }),
                    )
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`${translate("crm.projects.remove_member")} ${sale.first_name} ${sale.last_name}`}
                  onClick={() =>
                    run((dataProvider) =>
                      dataProvider.delete("sales_projects", {
                        id: row.id,
                        previousData: row,
                      }),
                    )
                  }
                >
                  <X className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {others.length ? (
          <select
            aria-label={translate("crm.projects.add_member")}
            value=""
            onChange={(event) => {
              const sale = others.find(
                (s) => String(s.id) === event.target.value,
              );
              if (!sale) return;
              run((dataProvider) =>
                dataProvider.create<SalesProject>("sales_projects", {
                  data: {
                    sales_id: sale.id,
                    project_id: project.id,
                    access: presetAccess(sale.role),
                  },
                }),
              );
            }}
            className="h-8 w-fit rounded-md border bg-card px-2 text-sm text-muted-foreground"
          >
            <option value="">+ {translate("crm.projects.add_member")}</option>
            {others.map((sale) => (
              <option key={sale.id} value={String(sale.id)}>
                {sale.first_name} {sale.last_name}
              </option>
            ))}
          </select>
        ) : null}
      </CardContent>
    </Card>
  );
};
